<template>
  <div>
    <h1 class="page-header">{{ $t('payslip.pageTitle') }}</h1>
    <div class="ts-flex ts--mx-4">
      <div class="ts-w-3/12 ts-px-4">
        <ts-card>
          <ts-card-wrapper>
            <div class="row">
              <div class="col-sm-12">
                <div class="form-group m-b-10">
                  <label
                    class="col-form-label control-label required"
                  >{{ $t('payslip.templateName') }}</label>
                  <div>
                    <ts-select
                      v-model="model.template_id"
                      :options="reportTemplates.map(t => ({ value: t.template_id, text: t.template_name }))"
                      :class="{ 'is-invalid': errors.has('template_id') }"
                    />
                    <div
                      class="invalid-feedback"
                      v-if="errors.has('template_id')"
                    >{{ errors.first('template_id') }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="ts-flex ts--mx-2">
              <div class="ts-w-1/2 ts-px-2">
                <div class="row">
                  <div class="col-sm-12">
                    <div class="form-group m-b-10">
                      <label class="col-form-label control-label">{{ $t('payslip.cycleYear') }}</label>
                      <div>
                        <ts-year-picker-input v-model="model.cycle_year" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="ts-w-1/2 ts-px-2">
                <div class="row">
                  <div class="col-sm-12">
                    <div class="form-group m-b-10">
                      <label class="col-form-label control-label">{{ $t('payslip.cycleMonth') }}</label>
                      <div>
                        <ts-month-picker-input
                          v-model="model.cycle_month"
                          format="M"
                          displayFormat="MMM"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12">
                <div class="form-group m-b-10">
                  <label class="col-form-label control-label required">{{ $t('payslip.employee') }}</label>
                  <div>
                    <ts-loading-banner :loading="loadingEmployees">
                      <ts-search-select
                        v-model="model.employee_id"
                        :options="employees.map(em => ({ value: em.employee_id, text: em.employee_name_en }))"
                        :filterFunction="employeeFilterFunction"
                        :class="{ 'is-invalid': errors.has('employee_id') }"
                      />
                    </ts-loading-banner>
                    <div
                      class="invalid-feedback"
                      v-if="errors.has('employee_id')"
                    >{{ errors.first('employee_id') }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row ts-mt-4">
              <div class="col-sm-12 col-lg-12 col-md-12 ts-text-right">
                <b-dropdown dropup variant="outline-default">
                  <template v-slot:button-content>
                    <i
                      class="fas fa-circle-notch spin inline-block mr-1"
                      v-if="exportingExcel || exportingPdf"
                    ></i>
                    <span class="inline-block">{{ $t('button.export') }}</span>
                  </template>
                  <b-dropdown-item href="#" @click="exportExcel">
                    <i class="fas fa-file-excel ts-text-green-500"></i>
                    {{ $t('button.excel') }}
                  </b-dropdown-item>
                  <b-dropdown-item href="#" @click="exportPdf">
                    <i class="fas fa-file-pdf ts-text-red-500"></i>
                    {{ $t('button.pdf') }}
                  </b-dropdown-item>
                </b-dropdown>
                <ts-button color="primary" class="ml-2" @click="viewReport" :waiting="viewing">
                  {{
                  $t('payslip.viewButton')
                  }}
                </ts-button>
              </div>
            </div>
          </ts-card-wrapper>
        </ts-card>
      </div>
      <div class="ts-w-9/12 ts-px-4">
        <ts-card>
          <ts-card-wrapper>
            <div class="overflow-auto" style="max-height: 80vh">
              <payslip-header :dataHeader="payslip.Header" v-if="payslip.Header" />
              <payslip-body :dataBody="payslip.Body" v-if="payslip.Body" />
              <payslip-footer :dataFooter="payslip.Footer" v-if="payslip.Footer" />
            </div>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { trim } from 'lodash'
import { mapState } from 'vuex'
import * as FileDownload from 'downloadjs'
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import PayslipHeader from './payslip-header.vue'
import PayslipBody from './payslip-body.vue'
import PayslipFooter from './payslip-footer.vue'
import PreviewPdf from './preview-pdf'

export default {
  name: 'payslip',
  components: { PayslipHeader, PayslipBody, PayslipFooter, PreviewPdf },
  data() {
    return {
      loading: false,
      loadingEmployees: false,
      exportingPdf: false,
      exportingExcel: false,
      viewing: false,
      errors: new Errors(),
      model: {
        template_id: '',
        employee_id: '',
        cycle_year: moment().year(),
        cycle_month: moment().month() + 1
      }
    }
  },
  computed: {
    ...mapState('hr/payslip', ['employees', 'reportTemplates', 'payslip'])
  },
  methods: {
    fetchData() {
      return this.$store.dispatch('hr/payslip/fetch', this.model)
    },
    employeeFilterFunction(search, options) {
      return options.filter(option => {
        return option.text.toLowerCase().includes(search.toLowerCase())
      })
    },
    viewReport(attributes) {
      this.errors = new Errors()
      this.viewing = true
      this.fetchData(attributes)
        .then(() => {
          this.viewing = false
        })
        .catch(error => {
          this.viewing = false
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
    },
    exportExcel() {
      this.errors = new Errors()
      this.exportingExcel = true

      this.$store
        .dispatch('hr/payslip/exportExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(response.headers['content-disposition'].indexOf('filename'))
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(err)))
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exportingExcel = false))
    },
    exportPdf() {
      this.$modal.show(
        PreviewPdf,
        {
          data: this.model
        },
        {
          height: 'auto',
          width: '70%',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        }
      )
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('hr/payslip/RESET_STATE')
    next()
  }
}
</script>
